import { Injectable } from "@angular/core";
import { BaseService } from "@dmms/core/http";
import { ResourceManagementEndpoints } from "../types/resource-management-endpoints";
import {
  IApiResponse,
  IApiResponsePagination,
  IBaseHttpResponse,
} from "@models/http/http-options";
import { Observable } from "rxjs";
import { IProperty } from "../models/property";
import {
  IEquipmentByFleet,
  IEquipmentByFleetFilter,
  IEquipmentDevicePayload,
  IEquipmentDeviceResponse,
  IEquipmentFeedback,
  IEquipmentTypePayload,
  IEquipmentTypePropertyResponse,
  IEquipmentTypeResponse,
  IEquipmentUtilizationThresholdPayload,
  IEquipmentUtilizationThresholdResponse,
} from "../models/equipment";
import {
  IEquipmentBpShowcase,
  IEquipmentBpShowcaseParams,
} from "../models/equipment-bp-showcase";
import { IPagination } from "@models/core/pagination";
import { IFleetAccessible } from "../models/fleet";
import {
  IUtilizationReportPayload,
  IUtilizationReportResponse,
} from "../models/equipment-utilization-report";
import { IReportResult } from "@models/reports/reports";
import { IActiveBookingApiItem } from "@modules/booking-requests";

@Injectable({
  providedIn: "root",
})
export class ResourceManagementApiService extends BaseService {
  createProperty(
    payload: Omit<IProperty, "id">,
    version = 1,
  ): Observable<IApiResponse<string>> {
    return this.post(
      `v${version}/${ResourceManagementEndpoints.Property}`,
      payload,
    );
  }

  // ───── Equipment Type ────────────────────────────────────────────────────────

  createEquipmentType(
    payload: IEquipmentTypePayload,
    version = 1,
  ): Observable<IApiResponse<IEquipmentTypeResponse>> {
    return this.post(
      `v${version}/${ResourceManagementEndpoints.EquipmentType}`,
      payload,
    );
  }

  updateEquipmentType(
    payload: IEquipmentTypePayload,
    version = 1,
  ): Observable<IApiResponse<IEquipmentTypeResponse>> {
    return this.put(
      `v${version}/${ResourceManagementEndpoints.EquipmentType}/${payload.id}`,
      payload,
    );
  }

  getEquipmentTypeProperties(
    equipmentTypeId: string,
    version = 1,
  ): Observable<IApiResponsePagination<IEquipmentTypePropertyResponse[]>> {
    return this.get(
      `v${version}/${ResourceManagementEndpoints.EquipmentType}/${equipmentTypeId}/properties`,
    );
  }

  // ───── Fleet Accessible ─────────────────────────────────────────────────────

  getFleetAccessible(
    params: IPagination,
    version = 1,
  ): Observable<IApiResponsePagination<IFleetAccessible[]>> {
    return this.get(
      `v${version}/${ResourceManagementEndpoints.FleetAccessible}`,
      params,
    );
  }

  // ───── Equipment by Fleet ───────────────────────────────────────────────────

  getEquipmentByFleet(
    params: IEquipmentByFleetFilter,
    version = 1,
  ): Observable<IApiResponsePagination<IEquipmentByFleet[]>> {
    return this.post(
      `v${version}/${ResourceManagementEndpoints.EquipmentByFleet}`,
      params,
    );
  }

  getEquipmentUtilizationReport(
    payload: IUtilizationReportPayload,
    version = 1,
  ): Observable<IUtilizationReportResponse> {
    return this.post(
      `v${version}/${ResourceManagementEndpoints.EquipmentUtilizationReport}`,
      payload,
    );
  }

  exportEquipmentUtilizationReport(
    payload: IUtilizationReportPayload,
    version = 1,
  ): Observable<IApiResponse<IReportResult>> {
    return this.post(
      `v${version}/${ResourceManagementEndpoints.EquipmentUtilizationReportExport}`,
      payload,
    );
  }

  getEquipmentFeedbackByEquipmentId(
    equipmentId: string,
    params: IPagination,
    version = 1,
  ): Observable<IApiResponsePagination<IEquipmentFeedback[]>> {
    return this.get(
      `v${version}/${ResourceManagementEndpoints.EquipmentFeedbackById.replace("{id}", equipmentId)}`,
      params,
    );
  }

  completeEquipmentState(
    id: string,
    version = 1,
  ): Observable<IBaseHttpResponse> {
    return this.post(
      `v${version}/${ResourceManagementEndpoints.EquipmentStateComplete.replace("{id}", id)}`,
      {},
    );
  }

  getActiveBookings(
    equipmentId: string,
    params?: { plannedStartDateTime?: string; plannedEndDateTime?: string },
    version = 1,
  ): Observable<IApiResponse<IActiveBookingApiItem[]>> {
    return this.get(
      `v${version}/${ResourceManagementEndpoints.BookingActive.replace("{equipmentId}", equipmentId)}`,
      params,
    );
  }

  getActiveBookingsForBooking(
    equipmentId: string,
    bookingId: string,
    params?: { plannedStartDateTime?: string; plannedEndDateTime?: string },
    version = 1,
  ): Observable<IApiResponse<IActiveBookingApiItem[]>> {
    return this.get(
      `v${version}/${ResourceManagementEndpoints.BookingActiveForBooking.replace("{equipmentId}", equipmentId).replace("{bookingId}", bookingId)}`,
      params,
    );
  }

  getEquipmentBpShowcase(
    params: IEquipmentBpShowcaseParams,
    version = 1,
  ): Observable<IApiResponsePagination<IEquipmentBpShowcase[]>> {
    return this.post(
      `v${version}/${ResourceManagementEndpoints.EquipmentBpShowcase}`,
      params,
    );
  }

  getEquipmentDevices(
    equipmentId: string,
    version = 1,
  ): Observable<IApiResponse<IEquipmentDeviceResponse[]>> {
    return this.get<IApiResponse<IEquipmentDeviceResponse[]>>(
      `v${version}/${ResourceManagementEndpoints.EquipmentDeviceListById.replace("{id}", equipmentId)}`,
    );
  }
  updateEquipmentDevices(
    equipmentId: string,
    params: IEquipmentDevicePayload,
    version = 1,
  ): Observable<IApiResponse<IBaseHttpResponse>> {
    return this.put(
      `v${version}/${ResourceManagementEndpoints.EquipmentDeviceById.replace("{id}", equipmentId)}`,
      params,
    );
  }

  exportAuditLog(
    equipmentId: string,
    params: { locale: string; timeZoneOffsetMinutes: number },
    version = 1,
  ): Observable<IApiResponse<IReportResult>> {
    return this.get(
      `v${version}/${ResourceManagementEndpoints.EquipmentAuditLogExport.replace("{id}", equipmentId)}`,
      params,
    );
  }

  getEquipmentThresholds(
    equipmentId: string,
    version = 1,
  ): Observable<IApiResponse<IEquipmentUtilizationThresholdResponse[]>> {
    return this.get<IApiResponse<IEquipmentUtilizationThresholdResponse[]>>(
      `v${version}/${ResourceManagementEndpoints.EquipmentUtilizationThreshold.replace("{id}", equipmentId)}`,
    );
  }

  putEquipmentThresholds(
    equipmentId: string,
    params: IEquipmentUtilizationThresholdPayload,
    version = 1,
  ): Observable<IBaseHttpResponse> {
    return this.put(
      `v${version}/${ResourceManagementEndpoints.EquipmentUtilizationThreshold.replace("{id}", equipmentId)}`,
      params,
    );
  }
}
